import { Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { AnalyticsBloc } from '@kanalytics';
import { EnvironmentVariablesService } from '@kenv';
import { GroupValidationBloc } from '@kp/auth/group-validation.bloc';
import { DataLinkBloc } from '@kp/data-link/data-link.bloc';
import { OnboardingBloc } from '@kp/onboarding/onboarding.bloc';
import { BrowserStorage, DataStoreService, OnboardingUtilities } from '@kservice';
import { Product, UserType } from '@ktypes/enums';
import { OnboardingData } from '@ktypes/models';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class GroupCodeIsValidGuard {
  constructor(
    private _activatedRoute: ActivatedRoute,
    private _analyticsBloc: AnalyticsBloc,
    private browserStorage: BrowserStorage,
    private dataStoreService: DataStoreService,
    private environmentVariablesService: EnvironmentVariablesService,
    private groupValidationBloc: GroupValidationBloc,
    private onboardingBloc: OnboardingBloc,
    private onboardingUtil: OnboardingUtilities,
    public router: Router
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> | boolean {
    if (DataLinkBloc.isExternalPulseSurvey) {
      this._analyticsBloc.userReadyForAnalytics();
      return true;
    }
    const authData = this.dataStoreService.authData;
    const savedOnboardingData: OnboardingData = this.browserStorage.getObject('onboarding');
    if (savedOnboardingData?.groupCode && !this.groupValidationBloc.groupValidationDataStatus) {
      this.groupValidationBloc.startValidatingGroupCode(savedOnboardingData.groupCode);
    }

    return this.groupValidationBloc.groupValidationDataStatus$.pipe(
      map((status) => {
        if (authData?.token && authData?.user?.type === UserType.user) {
          if (this.onboardingUtil.onboardingData?.passwordSetRequired) {
            return this.router.parseUrl('/welcome/signup/password');
          }
          this._analyticsBloc.userReadyForAnalytics();
          this.onboardingUtil.removeOnboardingData();
          return this.router.parseUrl('');
        }

        if (this.environmentVariablesService.product === Product.purposeful) {
          if (!status && savedOnboardingData && savedOnboardingData.groupCode) {
            // If the user manually navigates to any of the /welcome* routes or refreshes the page.
            // Uses navigate to allow for replaceUrl
            this._analyticsBloc.userReadyForAnalytics();
            void this.router.navigate([`/${savedOnboardingData.latestPage}`], { replaceUrl: true });
            return false;
          }
        }

        if (!status) {
          return this.router.parseUrl('/welcome/error');
        }

        // If the status is Status.starting, the user will be redirected
        // to onboarding/screen-landing and the result will be handled
        // there asynchronously.

        if (route.url.length !== 0) {
          this._analyticsBloc.userReadyForAnalytics();
        }
        return true;
      })
    );
  }
}
